<template>
  <Card variant="outline" size="medium">
    <View direction="column" gap="lg">
      <View direction="row" gap="md">
        <PropertyPhotoPreview :image-url="propertyInquiry.property.coverImage" size="sm" />

        <View direction="column" :grow="1" gap="xs">
          <View direction="row" justify="space-between" gap="md">
            <Text variant="label-medium">{{ propertyInquiry.property.displayName }}</Text>
            <Text variant="label-small" color="secondary">
              {{ useDateFormat(propertyInquiry.createdAt.toString(), 'MMM DD') }}
            </Text>
          </View>
          <View direction="row" gap="sm" align="center">
            <InquiryStatusLabel :small="true" :dot="false" :status="propertyInquiry.stage" />
            <div class="dot" v-if="propertyInquiry.source"></div>
            <Text variant="label-small" color="secondary" v-if="propertyInquiry.source">
              {{ propertyInquiry.source }}
            </Text>
          </View>
        </View>
      </View>

      <ShowingRequestStateCard
        v-if="isVisibleShowingRequestStateInfo"
        :showing-request-status="showingRequest?.status"
        :renter-name="renter.user.name"
      />

      <RescheduleRequestStateCard
        v-else-if="isVisibleRescheduleRequestStateInfo"
        :renter-name="renter.user.name"
        :showing-start-time="relatedShowing?.startTime?.toString()!"
        :showing-end-time="relatedShowing?.endTime?.toString()!"
        :reschedule-request-status="rescheduleRequest?.status!"
      />

      <View gap="sm" justify="end">
        <Button variant="outline" size="small" @click="emit('openConversationClicked')">See conversation</Button>

        <Button
          v-if="canSendApplication"
          variant="primary"
          size="small"
          :loading="isSendingApplication"
          @click="sendApplication"
        >
          Send application
        </Button>
      </View>

      <SendApplicationModal
        :renter="renter"
        :property-inquiry="propertyInquiry"
        :is-showing-happened="isShowingHappened"
        ref="renterProfileSendApplicationModalRef"
      />

      <ProvideInvestorAddressModal @addressSaved="handleAddressSaved" ref="provideInvestorAddressModalRef" />
    </View>
  </Card>
</template>

<script setup lang="ts">
import { useDateFormat } from '@vueuse/core';
import { computed, ref } from 'vue';

import { Button, Card, Text, View } from '@tallo/design-system';
import { useInvestorStore } from '@tallo/investor/investor';
import { InquiryStatusLabel, PropertyInquiryDto } from '@tallo/investor/property';
import {
  RescheduleRequestStatus,
  ShowingRequestStatus,
  RescheduleRequestStateCard,
  ShowingRequestStateCard,
  useShowingsQuery,
} from '@tallo/investor/showing';
import { PropertyPhotoPreview } from '@tallo/property';
import { RenterDto } from '@tallo/renter';

import { RenterRequest } from '../model/renter-request.interface';
import { useRenterProfileStore } from '../store/renter-profile.store';
import ProvideInvestorAddressModal from './provide-investor-address-modal/provide-investor-address-modal.vue';
import SendApplicationModal from './send-application-modal/send-application-modal.vue';

const props = defineProps<{ propertyInquiry: PropertyInquiryDto; renter: RenterDto }>();
const emit = defineEmits(['openConversationClicked']);

const showingsQuery = useShowingsQuery();
const renterProfileStore = useRenterProfileStore();
const investorStore = useInvestorStore();

const isSendingApplication = ref(false);
const renterProfileSendApplicationModalRef = ref<InstanceType<typeof SendApplicationModal>>();
const provideInvestorAddressModalRef = ref<InstanceType<typeof ProvideInvestorAddressModal>>();
const propertyId = computed(() => props.propertyInquiry.property.id);
const showings = computed(() => showingsQuery.data.value || []);
const renterRequest = computed(() => {
  const requestsData: RenterRequest = {
    relatedShowing: undefined,
    showingRequest: undefined,
    rescheduleRequest: undefined,
  };

  showings.value.find((showing) => {
    const showingReqForRenter = showing.showingRequests.find((showingRequest) => {
      return showingRequest.renter.id === props.renter.id && showing.property.id === propertyId.value;
    });

    if (showingReqForRenter) {
      requestsData.relatedShowing = showing;
      requestsData.showingRequest = showingReqForRenter;
      return true;
    }

    const rescheduleReqForRenter = showing.rescheduleRequests.find((rescheduleRequest) => {
      return rescheduleRequest.renter.id === props.renter.id && showing.property.id === propertyId.value;
    });

    if (rescheduleReqForRenter) {
      requestsData.relatedShowing = showing;
      requestsData.rescheduleRequest = rescheduleReqForRenter;
      return true;
    }

    return false;
  });

  return requestsData;
});
const rescheduleRequest = computed(() => renterRequest.value.rescheduleRequest);
const showingRequest = computed(() => renterRequest.value.showingRequest);
const relatedShowing = computed(() => renterRequest.value.relatedShowing);
const isShowingHappened = computed(() => showingRequest.value?.status === ShowingRequestStatus.ACCEPTED);
const isVisibleShowingRequestStateInfo = computed(() =>
  [
    ShowingRequestStatus.PENDING,
    ShowingRequestStatus.ACCEPTED,
    ShowingRequestStatus.DECLINED,
    ShowingRequestStatus.EXPIRED,
    ShowingRequestStatus.CANCELED_BY_INVESTOR,
    ShowingRequestStatus.CANCELED_BY_RENTER,
  ].includes(showingRequest.value?.status!),
);

const isVisibleRescheduleRequestStateInfo = computed(() =>
  [RescheduleRequestStatus.PENDING, RescheduleRequestStatus.DECLINED, RescheduleRequestStatus.CANCELED].includes(
    rescheduleRequest.value?.status!,
  ),
);

const canSendApplication = computed(() => !renterProfileStore.isApplicationSent(propertyId.value));

function sendApplication() {
  if (investorStore.address.address) {
    renterProfileSendApplicationModalRef.value?.modalRef?.show();
  } else {
    provideInvestorAddressModalRef.value?.modalRef?.show();
  }
}

function handleAddressSaved() {
  setTimeout(() => {
    renterProfileSendApplicationModalRef.value?.modalRef?.show();
  }, 800);
}
</script>

<style scoped lang="scss">
.showing-request-actions-card {
  max-width: 100%;
}

.dot {
  background-color: var(--t-text-color-secondary);
  width: var(--t-spacing-xs);
  height: var(--t-spacing-xs);
  border-radius: var(--t-border-radius-half-rounded);
}
</style>
